// Форма створення/редагування питання.
// Маршрут /admin/questions/new — створення; /admin/questions/:id — редагування.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  api,
  ApiError,
  LICENSE_CATEGORIES,
  type AdminAnswer,
  type AdminCategory,
  type AdminQuestion,
  type LicenseCategory,
} from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

type LocalAnswer = {
  // tempId для нових (без id), або реальний id
  key: string;
  id?: number;
  textUk: string;
  isCorrect: boolean;
  order: number;
};

const blankAnswers = (): LocalAnswer[] =>
  Array.from({ length: 4 }, (_, i) => ({
    key: `tmp_${i}_${Math.random().toString(36).slice(2)}`,
    textUk: '',
    isCorrect: i === 0,
    order: i,
  }));

export function AdminQuestionForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();

  const cats = useFetch<AdminCategory[]>(() => api.admin.listCategories(), []);
  const existing = useFetch<AdminQuestion & { answers: AdminAnswer[] } | null>(
    () => (isEdit ? api.admin.getQuestion(Number(id)) : Promise.resolve(null)),
    [id],
  );
  // id+категорія всіх питань у порядку списку — для лічильників і навігації.
  const nav = useFetch<{ items: Array<{ id: number; c: number }> }>(
    () => (isEdit ? api.admin.questionNavIds() : Promise.resolve({ items: [] })),
    [isEdit],
  );

  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [textUk, setTextUk] = useState('');
  const [explanation, setExplanation] = useState('');
  const [explanationCasual, setExplanationCasual] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [licenseCategories, setLicenseCategories] = useState<LicenseCategory[]>([]);
  const [answers, setAnswers] = useState<LocalAnswer[]>(blankAnswers());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Підвантажуємо існуюче питання у форму
  useEffect(() => {
    if (!existing.data) return;
    const q = existing.data;
    setCategoryId(q.categoryId);
    setTextUk(q.textUk);
    setExplanation(q.explanation ?? '');
    setExplanationCasual(q.explanationCasual ?? '');
    setImageUrl(q.imageUrl);
    setDifficulty(q.difficulty);
    setIsActive(q.isActive);
    setLicenseCategories(q.licenseCategories ?? []);
    setAnswers(
      q.answers.map((a) => ({
        key: `id_${a.id}`,
        id: a.id,
        textUk: a.textUk,
        isCorrect: a.isCorrect,
        order: a.order,
      })),
    );
  }, [existing.data]);

  function setAnswerField<K extends keyof LocalAnswer>(idx: number, key: K, value: LocalAnswer[K]) {
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx]!, [key]: value };
      return next;
    });
  }

  function setCorrectAnswer(idx: number) {
    setAnswers((prev) => prev.map((a, i) => ({ ...a, isCorrect: i === idx })));
  }

  function addAnswer() {
    if (answers.length >= 6) return;
    setAnswers((prev) => [
      ...prev,
      {
        key: `tmp_${prev.length}_${Math.random().toString(36).slice(2)}`,
        textUk: '',
        isCorrect: false,
        order: prev.length,
      },
    ]);
  }

  function removeAnswer(idx: number) {
    if (answers.length <= 2) return;
    setAnswers((prev) => prev.filter((_, i) => i !== idx).map((a, i) => ({ ...a, order: i })));
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const res = await api.admin.uploadImage(file);
      setImageUrl(res.url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не вдалося завантажити файл');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  // afterNav: 'back' — назад до списку; число — перейти до питання з цим id (Зберегти і далі)
  async function save(afterNav: 'back' | number) {
    setError(null);
    if (categoryId === '') {
      setError('Оберіть категорію');
      return;
    }
    const correctCount = answers.filter((a) => a.isCorrect).length;
    if (correctCount !== 1) {
      setError('Має бути рівно одна правильна відповідь');
      return;
    }
    if (answers.some((a) => !a.textUk.trim())) {
      setError('Усі варіанти відповідей мають бути заповнені');
      return;
    }

    setSaving(true);
    try {
      const payloadAnswers = answers.map((a) => ({
        ...(a.id ? { id: a.id } : {}),
        textUk: a.textUk.trim(),
        isCorrect: a.isCorrect,
        order: a.order,
      }));

      if (isEdit) {
        await api.admin.updateQuestion(Number(id), {
          categoryId,
          textUk: textUk.trim(),
          imageUrl,
          explanation: explanation.trim() || null,
          explanationCasual: explanationCasual.trim() || null,
          difficulty,
          isActive,
          licenseCategories,
          answers: payloadAnswers,
        });
      } else {
        await api.admin.createQuestion({
          categoryId,
          textUk: textUk.trim(),
          imageUrl,
          explanation: explanation.trim() || null,
          explanationCasual: explanationCasual.trim() || null,
          difficulty,
          licenseCategories,
          answers: payloadAnswers,
        });
      }
      if (typeof afterNav === 'number') {
        // Зберегти і далі: перехід до наступного питання (форма перезавантажиться за :id)
        setSaving(false);
        window.scrollTo(0, 0);
        navigate(`/admin/questions/${afterNav}`);
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/admin/questions');
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не вдалося зберегти');
      setSaving(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    void save('back');
  }

  async function handleDelete() {
    if (!isEdit || !confirm('Деактивувати це питання? Воно зникне з тестів.')) return;
    try {
      await api.admin.deleteQuestion(Number(id));
      if (window.history.length > 1) navigate(-1);
      else navigate('/admin/questions');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка');
    }
  }

  if (existing.loading || cats.loading) return <Loader />;
  if (existing.error) return <ErrorView error={existing.error} onRetry={existing.reload} />;
  if (cats.error) return <ErrorView error={cats.error} onRetry={cats.reload} />;

  // Позиція питання в загальному списку + сусіди (для навігації)
  const navItems = nav.data?.items ?? [];
  const curId = isEdit ? Number(id) : NaN;
  const pos = navItems.findIndex((x) => x.id === curId);
  const total = navItems.length;
  const prevId = pos > 0 ? navItems[pos - 1]!.id : null;
  const nextId = pos >= 0 && pos < total - 1 ? navItems[pos + 1]!.id : null;
  // Позиція всередині теми (категорії)
  const curCat = existing.data?.categoryId ?? (pos >= 0 ? navItems[pos]!.c : null);
  const topicItems = curCat != null ? navItems.filter((x) => x.c === curCat) : [];
  const topicPos = topicItems.findIndex((x) => x.id === curId) + 1; // 1-based (0 якщо не знайдено)
  const topicTotal = topicItems.length;
  const topicNum = existing.data?.category?.nameUk?.match(/^(\d+)/)?.[1] ?? null;
  const goTo = (qid: number) => {
    window.scrollTo(0, 0);
    navigate(`/admin/questions/${qid}`);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-4">
      {isEdit && total > 0 && pos >= 0 && (
        <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-1 flex items-center justify-between border-b border-tg-secondary-bg bg-tg-bg/95 px-4 py-2 text-sm backdrop-blur">
          <button
            type="button"
            disabled={!prevId}
            onClick={() => prevId && goTo(prevId)}
            className="rounded-lg px-2 py-1 font-medium text-tg-link disabled:opacity-30"
          >
            ‹ Попереднє
          </button>
          <div className="text-center leading-tight">
            <div className="font-semibold">Питання {pos + 1} з {total}</div>
            {topicTotal > 0 && topicPos > 0 && (
              <div className="text-[11px] text-tg-hint">
                Тема {topicNum ?? '?'} · питання {topicPos} з {topicTotal}
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={!nextId}
            onClick={() => nextId && goTo(nextId)}
            className="rounded-lg px-2 py-1 font-medium text-tg-link disabled:opacity-30"
          >
            Наступне ›
          </button>
        </div>
      )}

      {error && <div className="card border border-tg-destructive text-sm text-tg-destructive">{error}</div>}

      <div className="card space-y-2">
        <label className="block text-xs font-medium text-tg-hint">Категорія</label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
          className="w-full rounded-lg bg-tg-bg px-3 py-2"
          required
        >
          <option value="">Оберіть…</option>
          {cats.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nameUk}
            </option>
          ))}
        </select>
      </div>

      <div className="card space-y-2">
        <label className="block text-xs font-medium text-tg-hint">Текст питання</label>
        <textarea
          value={textUk}
          onChange={(e) => setTextUk(e.target.value)}
          rows={3}
          className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
          required
        />
      </div>

      <div className="card space-y-2">
        <label className="block text-xs font-medium text-tg-hint">Зображення (опційно)</label>
        {imageUrl ? (
          <div className="relative">
            <img src={imageUrl} alt="" className="w-full rounded-lg bg-tg-bg object-contain" />
            <button
              type="button"
              onClick={() => setImageUrl(null)}
              className="absolute right-2 top-2 rounded-full bg-tg-destructive px-2 py-1 text-xs text-white"
            >
              ✕ Прибрати
            </button>
          </div>
        ) : (
          <label className="block">
            <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} className="hidden" />
            <div className="grid place-items-center rounded-lg border-2 border-dashed border-tg-hint bg-tg-bg p-6 text-sm text-tg-hint">
              {uploading ? 'Завантаження…' : 'Натисни щоб обрати файл (PNG/JPG/WEBP, до 5 МБ)'}
            </div>
          </label>
        )}
      </div>

      <div className="card space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-tg-hint">Варіанти відповіді</label>
          <button type="button" onClick={addAnswer} disabled={answers.length >= 6} className="text-xs text-tg-link disabled:opacity-50">
            + Додати
          </button>
        </div>
        {answers.map((a, i) => (
          <div key={a.key} className="flex items-start gap-2">
            <button
              type="button"
              onClick={() => setCorrectAnswer(i)}
              className={`mt-2 grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs ${
                a.isCorrect ? 'bg-tg-success text-white' : 'bg-tg-bg text-tg-hint'
              }`}
              title="Правильна відповідь"
            >
              {a.isCorrect ? '✓' : ''}
            </button>
            <input
              value={a.textUk}
              onChange={(e) => setAnswerField(i, 'textUk', e.target.value)}
              placeholder={`Варіант ${i + 1}`}
              className="flex-1 rounded-lg bg-tg-bg px-3 py-2 text-sm"
              required
            />
            <button
              type="button"
              onClick={() => removeAnswer(i)}
              disabled={answers.length <= 2}
              className="mt-2 text-tg-hint disabled:opacity-30"
              title="Видалити"
            >
              ✕
            </button>
          </div>
        ))}
        <p className="text-xs text-tg-hint">
          Натисни ⚪ ліворуч щоб позначити правильну. Має бути рівно одна.
        </p>
      </div>

      <div className="card space-y-2">
        <label className="block text-xs font-medium text-tg-hint">
          🎓 Пояснення — інструктор (сайт + за замовчуванням)
        </label>
        <textarea
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          rows={4}
          className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
        />
        <label className="block text-xs font-medium text-tg-hint">
          😎 Пояснення — дружбан (розмовний стиль)
        </label>
        <textarea
          value={explanationCasual}
          onChange={(e) => setExplanationCasual(e.target.value)}
          rows={4}
          className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
        />
        <p className="text-[11px] text-tg-hint">
          Якщо змінити текст — 👍/👎 до цього пояснення обнуляються, і воно зникає зі списку
          проблемних. Оцінки почнуть збиратися заново.
        </p>
      </div>

      <div className="card space-y-2">
        <label className="block text-xs font-medium text-tg-hint">
          Категорії прав
        </label>
        <div className="flex flex-wrap gap-1.5">
          {LICENSE_CATEGORIES.map((c) => {
            const active = licenseCategories.includes(c);
            return (
              <button
                key={c}
                type="button"
                onClick={() =>
                  setLicenseCategories((prev) =>
                    prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
                  )
                }
                className={`min-w-12 rounded-lg px-3 py-1.5 text-sm font-medium ${
                  active ? 'bg-tg-button text-tg-button-text' : 'bg-tg-bg text-tg-text'
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-tg-hint">
          {licenseCategories.length === 0
            ? 'Не вибрано — питання буде доступне для всіх категорій.'
            : `Питання буде показано лише водіям ${licenseCategories.join(', ')}.`}
        </p>
      </div>

      <div className="card grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-tg-hint">Складність</label>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(Number(e.target.value))}
            className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
          >
            {[1, 2, 3, 4, 5].map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        {isEdit && (
          <label className="flex items-end gap-2 pb-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Активне
          </label>
        )}
      </div>

      <div className="space-y-2 pt-2">
        {isEdit && nextId && (
          <button
            type="button"
            disabled={saving}
            onClick={() => void save(nextId)}
            className="btn-primary w-full"
          >
            {saving ? 'Збереження…' : 'Зберегти і далі →'}
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className={`w-full ${isEdit && nextId ? 'btn-secondary' : 'btn-primary'}`}
        >
          {saving ? 'Збереження…' : isEdit ? 'Зберегти зміни' : 'Створити питання'}
        </button>
        {isEdit && (
          <button type="button" onClick={handleDelete} className="btn-secondary w-full text-tg-destructive">
            Деактивувати
          </button>
        )}
      </div>
    </form>
  );
}
